/*
dialogue box:
*/
import * as React from 'react';
import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { SearchBox, ISearchBoxStyles } from '@fluentui/react/lib/SearchBox';
import { useState } from 'react';
import { useBoolean } from '@fluentui/react-hooks';
import { _dataProvider } from '../dataProvider/DataProvider';
import { InView } from 'react-intersection-observer';

import { _smallScreen } from '../SharedCommon/utils';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

import { IChatMessage } from '../dataProvider/IChatData';
import './Org.css';
import './Dialog.css';
import { ResponsiveMode } from '@fluentui/react';

import { IUser } from '../dataProvider/DataProvider';
import { UserProfile } from '../SharedCommon/UserProfile';
import type { IContactItem } from '../dataProvider/IGroups';
import AddNewContactWindow from './AddNewContactWindow';
import closeIcon from '../img/close.png';
import backIcon from '../img/icon-Back.png';
import plusIcon from '../img/icon-Plus.png';
import OkIcon from '../img/ok.png';

import { AvatarImage, GroupImage } from '../SharedCommon/UserAvatarName';
import { Checkbox } from '@fluentui/react';
import { useUserGroups, IGroup } from '../hooks/useUserGroups';
import { COMMON_MODAL_STYLES } from './dialogStyles';

export { COMMON_MODAL_STYLES };

export const COMMON_SEARCH_BOX_STYLES: Partial<ISearchBoxStyles> = {
  root: {
    width: '100%',
    margin: '7px 0 10px 0',
    boxSizing: 'border-box',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0'
  },
  icon: {
    color: '#666666'
  }
};

const MEETING_ROOM_SEARCH_BOX_STYLES: Partial<ISearchBoxStyles> = {
  ...COMMON_SEARCH_BOX_STYLES,
  root: [COMMON_SEARCH_BOX_STYLES.root, { gridColumn: '1 / -1' }]
};

export const COMMON_HEADER_BUTTON_STYLES = {
  root: {
    padding: '8px',
    border: 'none',
    background: 'transparent',
    minWidth: '30px',
    height: '30px'
  },
  flexContainer: {
    height: '100%'
  }
};

export const COMMON_DIALOG_CONTENT_STYLES = {
  position: 'relative' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  height: '100%',
  maxHeight: '100vh',
  visibility: 'visible' as const,
  overflowY: 'auto' as const,
  overflowX: 'hidden' as const,
  overscrollBehavior: 'contain' as const,
  WebkitOverflowScrolling: 'touch' as const,
  padding: '0 16px',
  minHeight: 0
};

/**
 * 让 Dialog 的 `inner` / `innerContent` 两层成为可收缩的 flex column。
 * FluentUI 默认这两层不是 flex 容器，导致里面用 `flex: 1` 撑开的滚动区域失效
 * （高度随内容增长、永远不会溢出自身），于是 FluentUI 的触摸滚动处理会把
 * 整个弹窗的滚动都 preventDefault 掉——在手机浏览器里表现为“无法滚动”。
 * 把这组样式套到 `inner` 和 `innerContent` 上，`flex: 1` 的滚动区域才会真正
 * 被约束高度、成为可滚动元素（配合 data-is-scrollable 让触摸滚动命中它）。
 */
export const COMMON_DIALOG_SCROLLABLE_SLOT_STYLES = {
  display: 'flex',
  flexDirection: 'column' as const,
  flex: '1 1 auto',
  minHeight: 0
};

export const COMMON_GROUP_BUTTON_STYLES = {
  flexContainer: {
    justifyContent: 'left' as const,
    marginLeft: 0
  },
  root: {
    justifyContent: 'flex-start' as const,
    border: '1px solid #f5f5f5',
    borderRadius: '22px',
    padding: '10px',
    height: '42px',
    width: '100%',
    fontSize: '15px',
    boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)'
  },
  rootHovered: {
    backgroundColor: '#f5f5f5'
  }
};

const CONTACT_SEARCH_BOX_STYLES: Partial<ISearchBoxStyles> = {
  root: {
    width: '100%',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    marginTop: '15px',
    marginBottom: '0',
    padding: '4px'
  },
  field: {
    backgroundColor: 'transparent'
  },
  icon: {
    color: '#666666'
  }
};

const options: IChoiceGroupOption[] = [
  { key: 'A', text: 'Option A' },
  { key: 'B', text: 'Option B' },
  { key: 'C', text: 'Option C', disabled: true }
];
const modelProps = {
  isBlocking: false,
  styles: { main: { maxWidth: 450, borderRadius: '20px' } }
};

const dialogContentProps = {
  type: DialogType.largeHeader,
  showCloseButton: true,
  title: 'All emails together',
  subText: 'Your Inbox has changed. No longer does it include favorites, it is a singular destination for your emails.'
};

export const DialogLargeHeaderExample: React.FunctionComponent = () => {
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(false);

  return (
    <>
      <DefaultButton secondaryText='Opens the Sample Dialog' onClick={toggleHideDialog} text='Open Dialog' />
      <Dialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        dialogContentProps={dialogContentProps}
        modalProps={modelProps}>
        <ChoiceGroup defaultSelectedKey='B' options={options} />
        <DialogFooter>
          <PrimaryButton onClick={toggleHideDialog} text='Save' />
          <DefaultButton onClick={toggleHideDialog} text='Cancel' />
        </DialogFooter>
      </Dialog>
    </>
  );
};

const promptExternalLinkProps = {
  type: DialogType.largeHeader,
  showCloseButton: true,
  title: i18n.t('authoring.EnterLink')
  //subText: "subText -- TBD",
};

export interface IPromptExternalLinkProps {
  label: string;
  url: string;
  onExit: any;
  idx: number;
}

export const PromptExternalLink: React.FunctionComponent<IPromptExternalLinkProps> = (props) => {
  const [{ url, label }, setLabelUrl] = useState({
    url: props.url,
    label: props.label
  });
  //let url = props.url;
  //let label = props.label;

  return (
    <Dialog
      hidden={false}
      onDismiss={() => props.onExit(undefined)}
      dialogContentProps={promptExternalLinkProps}
      modalProps={modelProps}>
      <h3 style={{ textAlign: 'left' }}>{i18n.t('authoring.ContentTitle')}</h3>
      <input
        key={i18n.t('authoring.ContentTitle')}
        value={label}
        onChange={(ev) => setLabelUrl({ url, label: ev.target.value })}
        style={{ width: '100%' }}></input>
      <h3 style={{ textAlign: 'left' }}>{i18n.t('authoring.UrlLink')}</h3>
      <input
        key={i18n.t('authoring.UrlLink')}
        value={url}
        onChange={(ev) => setLabelUrl({ url: ev.target.value, label })}
        style={{ width: '100%' }}></input>
      <DialogFooter>
        <PrimaryButton
          onClick={() => {
            props.onExit(url, label || i18n.t('authoring.ExternalLink'), props.idx);
          }}
          text={i18n.t('authoring.Save')}
        />
        <DefaultButton onClick={() => props.onExit(undefined)} text={i18n.t('authoring.Cancel')} />
      </DialogFooter>
    </Dialog>
  );
};

const promptDocumentLabel = {
  type: DialogType.largeHeader,
  showCloseButton: true,
  title: i18n.t('authoring.EnterTitle')
  //subText: "subText -- TBD",
};

export interface IPromptDocumentLabelProps {
  label: string;
  onExit: any;
  idx: number;
  showPdfHtmlConversion?: boolean;
}

type PdfHtmlMode = 'none' | 'auto' | 'custom';

export const PromptDocumentLabel: React.FunctionComponent<IPromptDocumentLabelProps> = (props) => {
  const [{ label, htmlMode }, setDocumentOptions] = useState<{ label: string; htmlMode: PdfHtmlMode }>({
    label: props.label,
    htmlMode: 'none'
  });
  //let url = props.url;
  //let label = props.label;

  return (
    <Dialog
      hidden={false}
      onDismiss={() => props.onExit(undefined)}
      dialogContentProps={promptDocumentLabel}
      modalProps={modelProps}>
      <h3 style={{ textAlign: 'left' }}>{i18n.t('authoring.DocTitle')}</h3>
      <input
        key='Label'
        value={label}
        onChange={(ev) => setDocumentOptions({ label: ev.target.value, htmlMode })}
        style={{ width: '100%' }}></input>
      {!!props.showPdfHtmlConversion && (
        <div style={{ marginTop: 16, textAlign: 'left' }}>
          <ChoiceGroup
            label={i18n.t('authoring.PdfHtmlVersion')}
            selectedKey={htmlMode}
            options={[
              { key: 'none', text: i18n.t('authoring.PdfOnly') },
              { key: 'auto', text: i18n.t('authoring.ConvertPdfToHtml') },
              { key: 'custom', text: i18n.t('authoring.UploadCustomHtml') }
            ]}
            onChange={(_event, option) =>
              setDocumentOptions({
                label,
                htmlMode: (option?.key as PdfHtmlMode) || 'none'
              })
            }
          />
          {htmlMode === 'auto' && (
            <div style={{ color: '#666', marginTop: 6, marginLeft: 28, fontSize: 13, lineHeight: 1.5 }}>
              {i18n.t('authoring.ConvertPdfToHtmlHelp')}
            </div>
          )}
          {htmlMode === 'custom' && (
            <div style={{ color: '#666', marginTop: 6, marginLeft: 28, fontSize: 13, lineHeight: 1.5 }}>
              {i18n.t('authoring.UploadCustomHtmlHelp')}
            </div>
          )}
        </div>
      )}
      <DialogFooter>
        <PrimaryButton
          onClick={() => {
            props.onExit(label, props.idx, htmlMode);
          }}
          text={i18n.t('authoring.Save')}
        />
        <DefaultButton onClick={() => props.onExit(props.label, props.idx, 'none')} text={i18n.t('authoring.Cancel')} />
      </DialogFooter>
    </Dialog>
  );
};

const promptForScriptProps = {
  type: DialogType.largeHeader,
  showCloseButton: true,
  title: i18n.t('authoring.EnterScript')
  //subText: "subText -- TBD",
};

export interface IPromptForScriptProps {
  jsonTxt: string;
  onExit: any;
  idx: number;
  iii: number;
}

export const PromptForScript: React.FunctionComponent<IPromptForScriptProps> = (props) => {
  const [jsonTxt, setJsonTxt] = useState(props.jsonTxt);
  return (
    <Dialog
      hidden={false}
      onDismiss={() => props.onExit(undefined)}
      dialogContentProps={promptForScriptProps}
      modalProps={modelProps}>
      <textarea
        key='description'
        style={{ width: '100%', resize: 'vertical' }}
        rows={6}
        value={jsonTxt}
        placeholder={i18n.t('authoring.HereEnterScript')}
        onChange={(ev) => setJsonTxt(ev.target.value)}
      />
      <DialogFooter>
        <PrimaryButton
          onClick={() => {
            props.onExit(jsonTxt, props.idx, props.iii);
          }}
          text={i18n.t('authoring.Save')}
        />
        <DefaultButton onClick={() => props.onExit(undefined)} text={i18n.t('authoring.Cancel')} />
      </DialogFooter>
    </Dialog>
  );
};

export interface IPromptPickGroupProps {
  pickGroup: any;
}

/*
    this._dataProvider.getGroups().then((groups)=>{
      if (groups) {
        alert("您暂没有加入小组. 请在移动终端, 用这个链接 https://idigest.app/install 下载idigest软件APP, 注册后, 创建小组或加入小组, 就可以有线上小组电话了");
        return;
      }

      /*
      return <DialogLargeHeaderExample/>;
          {!!this.state.externLinkProps && (
            <PromptExternalLink
              url={this.state.externLinkProps.url}
              label={this.state.externLinkProps.label}
              onExit={this.state.externLinkProps.onExit}
              idx={this.state.externLinkProps.idx}
            />
          )}

      //this._dataProvider.getGroups();
const prompt4Group = {
  type: DialogType.largeHeader,
  title: "请选择小组",
  //title: "Please pick a small group by click",
  //subText: "subText -- TBD",
};
*/

export const PromptPickGroup: React.FunctionComponent<IPromptPickGroupProps> = (props) => {
  const { groupList, filteredGroups, txt, loading, searchQuery, setSearchQuery } = useUserGroups();
  const { t } = useTranslation();

  return (
    <Dialog
      hidden={false}
      responsiveMode={ResponsiveMode.small}
      onDismiss={() => {
        props.pickGroup(undefined);
        setSearchQuery('');
      }}
      dialogContentProps={{
        showCloseButton: false,
        styles: {
          inner: {
            padding: '0px'
          },
          header: {
            height: '0px'
          },
          content: COMMON_DIALOG_CONTENT_STYLES
        }
      }}
      modalProps={{
        isBlocking: true,
        styles: {
          main: COMMON_MODAL_STYLES
        },
        containerClassName: 'PickMeetingDialogue'
      }}>
      <div className='header-section'>
        <div className='button-wrapper right-btn'>
          <DefaultButton
            className='header-button'
            styles={COMMON_HEADER_BUTTON_STYLES}
            onClick={() => {
              props.pickGroup(undefined);
              setSearchQuery('');
            }}>
            <img src={closeIcon} alt='Close' className='button-icon' />
          </DefaultButton>
          <span className='button-label'>{t('Close')}</span>
        </div>
        <h3 style={{ textAlign: 'center', fontWeight: 450 }}>
          {groupList.length > 0
            ? t('ChooseGroup')
            : loading
              ? t('loading')
              : txt || t('NoGroup')}
        </h3>
        <br></br>
        {groupList.length > 0 && (
          <SearchBox
            styles={MEETING_ROOM_SEARCH_BOX_STYLES}
            value={searchQuery}
            placeholder={t('Search')}
            onEscape={() => setSearchQuery('')}
            onChange={(_, value) => setSearchQuery(value || '')}
            onClear={() => setSearchQuery('')}
          />
        )}
      </div>
      <div
        className='PickMeeting fellowshipGroup'
        style={{ overflowY: 'auto', overflowX: 'hidden', height: 'calc(100vh - 200px)' }}
        data-is-scrollable='true'>
        <ul className='group-list'>
          {!!groupList &&
            filteredGroups.map((item: IGroup) => (
              <li key={item.groupId} className='buttonColor'>
                <InView triggerOnce>
                  {({ inView, ref }) => (
                    <div ref={ref}>
                      <DefaultButton
                        className='orgGroupButton'
                        styles={COMMON_GROUP_BUTTON_STYLES}
                        onClick={() => props.pickGroup(item.groupId)}>
                        {inView && <GroupImage item={item} className='groupSymbol' />}
                        {item.name}
                      </DefaultButton>
                    </div>
                  )}
                </InView>
              </li>
            ))}
        </ul>
      </div>
    </Dialog>
  );
};

export interface IPromptPickShareTargetProps {
  onConfirm: (selectedGroupIds: number[]) => void;
  onDismiss: () => void;
}

export const PromptPickShareTarget: React.FunctionComponent<IPromptPickShareTargetProps> = (props) => {
  const { groupList, filteredGroups, txt, loading, searchQuery, setSearchQuery } = useUserGroups();
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  const { t } = useTranslation();

  const handleGroupToggle = (groupId: number, checked: boolean) => {
    setSelectedGroupIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(groupId);
      } else {
        newSet.delete(groupId);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    props.onConfirm(Array.from(selectedGroupIds));
  };

  const handleDismiss = () => {
    setSelectedGroupIds(new Set());
    setSearchQuery('');
    props.onDismiss();
  };

  return (
    <Dialog
      hidden={false}
      responsiveMode={ResponsiveMode.small}
      onDismiss={handleDismiss}
      dialogContentProps={{
        showCloseButton: false,
        styles: {
          inner: {
            padding: '0px'
          },
          header: {
            height: '0px'
          },
          content: COMMON_DIALOG_CONTENT_STYLES
        }
      }}
      modalProps={{
        isBlocking: true,
        styles: {
          main: COMMON_MODAL_STYLES
        },
        containerClassName: 'PickMeetingDialogue'
      }}>
      <div className='header-section'>
        <div className='button-wrapper left-btn'>
          <DefaultButton className='header-button' styles={COMMON_HEADER_BUTTON_STYLES} onClick={handleDismiss}>
            <img src={backIcon} alt='Back' className='button-icon' />
          </DefaultButton>
          <span className='button-label'>{t('Back')}</span>
        </div>
        <h3 style={{ textAlign: 'center', fontWeight: 450 }}>
          {groupList.length > 0
            ? selectedGroupIds.size > 0
              ? t('Chatroom.SelectedShareTargetsCount', { count: selectedGroupIds.size })
              : t('Chatroom.SelectShareTarget')
            : loading
              ? t('loading')
              : txt || t('NoGroup')}
        </h3>
        <div className='button-wrapper right-btn'>
          <DefaultButton
            className='header-button'
            styles={{
              ...COMMON_HEADER_BUTTON_STYLES,
              rootDisabled: {
                opacity: 0.4,
                cursor: 'not-allowed'
              }
            }}
            onClick={handleConfirm}
            disabled={selectedGroupIds.size === 0}>
            <img src={OkIcon} alt={t('OK')} className='button-icon' />
          </DefaultButton>
          <span className='button-label' style={{ opacity: selectedGroupIds.size > 0 ? 1 : 0.4 }}>
            {t('OK')}
          </span>
        </div>
        <br></br>
        {groupList.length > 0 && (
          <SearchBox
            styles={COMMON_SEARCH_BOX_STYLES}
            value={searchQuery}
            placeholder={t('Search')}
            onEscape={() => setSearchQuery('')}
            onChange={(_, value) => setSearchQuery(value || '')}
            onClear={() => setSearchQuery('')}
          />
        )}
      </div>
      <div
        className='PickMeeting fellowshipGroup'
        style={{ flex: '1 1 auto', overflowY: 'visible', overflowX: 'hidden', minHeight: 0 }}>
        <ul className='group-list'>
          {!!groupList &&
            filteredGroups.map((item: IGroup) => {
              const isSelected = selectedGroupIds.has(item.groupId);
              return (
                <li key={item.groupId} className='buttonColor'>
                  <InView triggerOnce>
                    {({ inView, ref }) => (
                      <div
                        ref={ref}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%'
                        }}>
                        <div
                          style={{ marginRight: '8px', cursor: 'pointer' }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                          }}
                          onMouseDown={(ev) => {
                            ev.stopPropagation();
                          }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={(ev, checked) => {
                              if (ev) {
                                ev.stopPropagation();
                              }
                              if (checked !== undefined) {
                                handleGroupToggle(item.groupId, checked);
                              }
                            }}
                            styles={{
                              checkbox: {
                                width: '17px',
                                height: '17px',
                                backgroundColor: 'white',
                                borderColor: '#b0b0b0',
                                cursor: 'pointer',
                                selectors: {
                                  '&:hover': {
                                    backgroundColor: 'white',
                                    borderColor: '#b0b0b0'
                                  }
                                }
                              },
                              checkmark: {
                                color: 'black !important',
                                fontWeight: 'bold !important'
                              }
                            }}
                          />
                        </div>
                        <DefaultButton
                          className='orgGroupButton'
                          styles={{
                            flexContainer: {
                              justifyContent: 'left',
                              marginLeft: 0
                            },
                            root: {
                              justifyContent: 'flex-start',
                              border: '1px solid #f5f5f5',
                              borderRadius: '22px',
                              padding: '10px',
                              height: '42px',
                              width: '100%',
                              fontSize: '15px',
                              boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
                              flex: 1
                            },
                            rootHovered: {
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                          onClick={() => handleGroupToggle(item.groupId, !isSelected)}>
                          {inView && <GroupImage item={item} className='groupSymbol' />}
                          {item.name}
                        </DefaultButton>
                      </div>
                    )}
                  </InView>
                </li>
              );
            })}
        </ul>
      </div>
    </Dialog>
  );
};

export interface IPromptPickFilesInGroupProps {
  groupId: number;
  processFile: (groupId: number, fileId: number) => Promise<void>;
  onDismiss: () => void;
}

export const IPromptPickFilesInGroup: React.FunctionComponent<IPromptPickFilesInGroupProps> = (props) => {
  const [fileList, setFileList] = useState<IChatMessage[] | undefined | null>(undefined);
  const { t } = useTranslation();
  const [dayRange, setDayRange] = useState(36);

  React.useEffect(() => {
    const getFiles = async () => {
      const files = await _dataProvider.getFileListInGroup(props.groupId, dayRange);
      setFileList(files);
    };
    getFiles();
  }, [dayRange, props.groupId]);

  const IDigestLogo = _dataProvider.getLogo();

  const fileName = (name: string) => {
    if (name.startsWith('文件 ') || name.startsWith('音频 ') || name.startsWith('图片 ')) {
      return name.slice(3);
    }
    return name;
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={() => {
        props.onDismiss();
      }}
      dialogContentProps={{
        showCloseButton: true
      }}
      modalProps={{
        isBlocking: false,
        styles: { main: { borderRadius: '20px' } },
        containerClassName: 'PickGroupDialogue'
      }}>
      <img alt='iDigestApp' className='centeriDigestLogo' src={IDigestLogo} />
      <br></br>
      <br></br>
      <h3 style={{ textAlign: 'center' }}>
        {fileList === undefined
          ? 'Loading...'
          : fileList === null
          ? 'Server error, please try again'
          : fileList.length === 0
          ? t('NoFile')
          : t('ChooseFile')}
      </h3>
      <div className='PickGroup' style={{ marginLeft: 0 }}>
        {!!fileList &&
          fileList.map((item: any, index) => (
            <ul key={index} className='buttonColor' style={{ padding: 0 }}>
              <DefaultButton
                className='orgGroupButton'
                style={{ width: '100%', padding: 4, borderRadius: 0 }}
                styles={{
                  flexContainer: {
                    marginLeft: 8
                  }
                }}
                onClick={() => props.processFile(props.groupId, item.fileId)}>
                {!_smallScreen && (
                  <div style={{ width: '100%' }}>
                    <p style={{ float: 'left' }}>{new Date(item.time * 1000).toLocaleDateString()}</p>
                    <p style={{ float: 'right' }}>{fileName(item.displayName)}</p>
                  </div>
                )}
                {_smallScreen && fileName(item.displayName)}
              </DefaultButton>
            </ul>
          ))}
        {dayRange === 36 && !!fileList && fileList.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <DefaultButton
              className='orgGroupButton'
              style={{ backgroundColor: 'lightgray', marginTop: 25 }}
              styles={{
                flexContainer: {
                  marginLeft: 25
                }
              }}
              onClick={() => setDayRange(178)}>
              {i18n.t('Chatroom.HalfYear')}
            </DefaultButton>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export interface IPromptContactListProps {
  isOpen: boolean;
  onDismiss: () => void;
  contactList: IContactItem[];
  currentUser: IUser;
  launchOneOnOneWithFriend: any;
}

export const PromptContactList: React.FC<IPromptContactListProps> = (props) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [showFriend, setShowFriend] = React.useState<IUser>();
  const [showAddNewContact, setShowAddNewContact] = React.useState(false);

  const filteredContacts = props.contactList.filter((contact) => {
    return !searchQuery || (contact.name || contact.displayName).toLowerCase().includes(searchQuery.toLowerCase());
  });

  const groupedContacts = filteredContacts.reduce((groups, contact) => {
    const letter = contact.displayName[0].toUpperCase();
    if (!groups[letter]) {
      groups[letter] = [];
    }
    groups[letter].push(contact);
    return groups;
  }, {} as Record<string, IContactItem[]>);

  const dialogContentProps = {
    showCloseButton: false,
    styles: {
      inner: {
        padding: '0px'
      },
      header: {
        height: '0px'
      },
      content: COMMON_DIALOG_CONTENT_STYLES
    }
  };

  const modalProps = {
    isBlocking: false,
    styles: {
      main: COMMON_MODAL_STYLES
    },
    containerClassName: 'PickMeetingDialogue'
  };

  const handleDismiss = () => {
    setShowFriend(undefined);
    props.onDismiss();
  };

  const renderHeader = () => {
    if (showFriend) {
      return (
        <div className='header-section'>
          <div className='button-wrapper left-btn'>
            <DefaultButton
              className='header-button'
              styles={COMMON_HEADER_BUTTON_STYLES}
              onClick={() => setShowFriend(undefined)}>
              <img src={backIcon} alt='Back' className='button-icon' />
            </DefaultButton>
            <span className='button-label'>{t('Back')}</span>
          </div>
          <div className='button-wrapper right-btn'>
            <DefaultButton className='header-button' styles={COMMON_HEADER_BUTTON_STYLES} onClick={handleDismiss}>
              <img src={closeIcon} alt='Close' className='button-icon' />
            </DefaultButton>
            <span className='button-label'>{t('Close')}</span>
          </div>
        </div>
      );
    }

    return (
      <div className='header-section'>
        <div className='button-wrapper left-btn'>
          <DefaultButton className='header-button' styles={COMMON_HEADER_BUTTON_STYLES} onClick={handleDismiss}>
            <img src={backIcon} alt='Back' className='button-icon' />
          </DefaultButton>
          <span className='button-label'>{t('Back')}</span>
        </div>
        <div className='button-wrapper right-btn'>
          <DefaultButton
            className='header-button'
            styles={COMMON_HEADER_BUTTON_STYLES}
            onClick={() => setShowAddNewContact(true)}>
            <img src={plusIcon} alt='Add Contact' className='button-icon' />
          </DefaultButton>
          <span className='button-label'>{t('AddNewContact')}</span>
        </div>
        <h3 style={{ textAlign: 'center', fontWeight: 600, fontSize: '18px' }}>{t('Contact')}</h3>
        <br></br>
        <SearchBox
          styles={CONTACT_SEARCH_BOX_STYLES}
          placeholder={t('Search')}
          value={searchQuery}
          onChange={(_, value) => setSearchQuery(value || '')}
          onClear={() => setSearchQuery('')}
        />
      </div>
    );
  };

  return (
    <Dialog
      hidden={!props.isOpen}
      responsiveMode={ResponsiveMode.small}
      onDismiss={props.onDismiss}
      dialogContentProps={dialogContentProps}
      modalProps={modalProps}>
      {renderHeader()}
      {!showFriend ? (
        <>
          <div
            className='contact-list fellowshipGroup'
            style={{ overflowY: 'auto', overflowX: 'hidden', height: 'calc(100vh - 200px)' }}
            data-is-scrollable='true'>
            {Object.keys(groupedContacts).map((letter) => (
              <div key={letter}>
                {groupedContacts[letter]
                  .filter((user) => user.loginId !== props.currentUser.uniqueId)
                  .map((contact, index) => (
                    <InView key={index * 1000}>
                      {({ inView, ref }) => (
                        <ul key={index} ref={ref} style={{ listStyle: 'none', padding: 0, margin: '8px 0' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <DefaultButton
                              className='buttonFellowship'
                              style={{
                                width: '100%',
                                backgroundColor: 'white',
                                border: 'none',
                                paddingLeft: 10
                              }}
                              styles={{
                                flexContainer: {
                                  justifyContent: 'left',
                                  marginLeft: 0
                                },
                                root: {
                                  justifyContent: 'flex-start',
                                  padding: '0 20px',
                                  width: '100%',
                                  fontSize: '15px',
                                  color: 'dark grey'
                                },
                                label: {
                                  textAlign: 'left'
                                }
                              }}
                              onClick={() => {
                                const user = contact as IUser;
                                user.tag = _dataProvider.getMyTagForUser(user.userId);
                                user.block = _dataProvider.getMyBlockForUser(user.userId);
                                setShowFriend(user);
                              }}>
                              {inView && <AvatarImage userId={contact.userId} className='groupSymbol' />}
                              {contact.name || contact.displayName}
                            </DefaultButton>
                          </div>
                        </ul>
                      )}
                    </InView>
                  ))}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ padding: '0 35px 20px 35px' }}>
          <UserProfile
            user={showFriend}
            updateFrameStyle={{
              marginTop: '20px'
            }}
            launchOneOnOneWithFriend={props.launchOneOnOneWithFriend}
            onDismiss={handleDismiss}
          />
        </div>
      )}
      {showAddNewContact && (
        <AddNewContactWindow
          onClose={() => setShowAddNewContact(false)}
          launchOneOnOneWithFriend={props.launchOneOnOneWithFriend}
        />
      )}
    </Dialog>
  );
};
